import { useState, useEffect } from 'react';
import Sidebar from '../Components/Sidebar';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend, AreaChart, Area, BarChart, Bar
} from 'recharts';
import { Users, Server, CheckCircle, TrendingUp, AlertTriangle, Zap, Activity, Shield, ArrowUpRight, ArrowDownRight, Calendar } from 'lucide-react';
import apiClient from '../Intercepter/APiClient';

export default function Dashboard() {
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState('7d');
  
  const weeklyData = [
    { name: 'Mon', requests: 1200, success: 1180, failed: 20, users: 240 },
    { name: 'Tue', requests: 1400, success: 1350, failed: 50, users: 290 },
    { name: 'Wed', requests: 1800, success: 1720, failed: 80, users: 320 },
    { name: 'Thu', requests: 1600, success: 1550, failed: 50, users: 270 },
    { name: 'Fri', requests: 2200, success: 2150, failed: 50, users: 380 },
    { name: 'Sat', requests: 1800, success: 1750, failed: 50, users: 250 },
    { name: 'Sun', requests: 1200, success: 1150, failed: 50, users: 210 },
  ];

  const errorBreakdown = [
    { name: 'Rate Limit', value: 35, color: '#F59E0B' },
    { name: 'Invalid Key', value: 25, color: '#EF4444' },
    { name: 'Server Error', value: 20, color: '#8B5CF6' },
    { name: 'Timeout', value: 15, color: '#06B6D4' },
    { name: 'Other', value: 5, color: '#6B7280' }
  ];

  const COLORS = ['#10B981', '#EF4444'];

  useEffect(() => {
    const fetchAnalytics = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get('/api/analytics');
        setAnalytics(response.data);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
        setError('Failed to fetch analytics data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, []);

  const getPieData = () => {
    if (!analytics) return [];
    return [
      { name: 'Successful', value: parseFloat(analytics?.api_success_rate?.successful || 95) },
      { name: 'Failed', value: parseFloat(analytics?.api_success_rate?.failed || 5) }
    ];
  };

  const parseErrorJson = (errorJson) => {
    try {
      return JSON.parse(errorJson).error;
    } catch (e) {
      return errorJson;
    }
  };

  const StatMetric = ({ label, value, change, icon: Icon, trend, color }) => (
    <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all group">
      <div className="flex justify-between items-start">
        <div>
          <p className="text-gray-400 text-sm font-medium mb-1">{label}</p>
          <p className="text-3xl font-bold text-white">{value}</p>
          {change && (
            <div className={`flex items-center gap-1 mt-2 text-sm font-semibold ${trend === 'up' ? 'text-green-400' : 'text-red-400'}`}>
              {trend === 'up' ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
              {change}
            </div>
          )}
        </div>
        <div className={`p-3 rounded-lg bg-gradient-to-br ${color} group-hover:scale-110 transition-transform`}>
          <Icon size={24} className="text-white" />
        </div>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin inline-block h-12 w-12 border-4 border-cyan-500 border-t-transparent rounded-full mb-4"></div>
            <p className="text-gray-300">Loading analytics…</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <Sidebar />
      <div className="flex-1 overflow-auto">
        <div className="text-white">
          {/* Header */}
          <div className="sticky top-0 z-10 backdrop-blur-lg border-b border-white/10">
            <div className="px-8 py-6">
              <div className="flex justify-between items-center">
                <div>
                  <h1 className="text-4xl font-black bg-gradient-to-r from-cyan-400 via-blue-400 to-purple-500 bg-clip-text text-transparent mb-2">
                    Analytics Dashboard
                  </h1>
                  <p className="text-gray-400">Real-time API performance metrics & insights</p>
                </div>
                <div className="flex gap-3">
                  {['24h', '7d', '30d'].map(period => (
                    <button
                      key={period}
                      onClick={() => setDateRange(period)}
                      className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                        dateRange === period
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg shadow-cyan-500/50'
                          : 'bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10'
                      }`}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="p-8 space-y-8">
            {error && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 flex items-center gap-3">
                <AlertTriangle className="text-red-400" size={20} />
                <p className="text-red-200">{error}</p>
              </div>
            )}

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatMetric
                label="Total Users"
                value={analytics?.total_users || 0}
                change="+12.5%"
                trend="up"
                icon={Users}
                color="from-blue-500 to-cyan-500"
              />
              <StatMetric
                label="Total Requests"
                value={(analytics?.total_requests || 0).toLocaleString()}
                change="+8.2%"
                trend="up"
                icon={Zap}
                color="from-green-500 to-emerald-500"
              />
              <StatMetric
                label="Success Rate"
                value={`${analytics?.api_success_rate?.successful || 95}%`}
                change="+2.1%"
                trend="up"
                icon={CheckCircle}
                color="from-purple-500 to-pink-500"
              />
              <StatMetric
                label="Active APIs"
                value="24"
                change="+3"
                trend="up"
                icon={Activity}
                color="from-orange-500 to-red-500"
              />
            </div>

            {/* Charts Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* API Requests Trend */}
              <div className="lg:col-span-2 bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-white">API Requests Trend</h3>
                    <p className="text-gray-400 text-sm mt-1">Last 7 days performance</p>
                  </div>
                  <TrendingUp className="text-green-400" size={24} />
                </div>
                <ResponsiveContainer width="100%" height={300}>
                  <AreaChart data={weeklyData}>
                    <defs>
                      <linearGradient id="colorRequests" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#06B6D4" stopOpacity={0.3}/>
                        <stop offset="95%" stopColor="#06B6D4" stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="name" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #4B5563', borderRadius: '8px' }}
                      labelStyle={{ color: '#F3F4F6' }}
                    />
                    <Area type="monotone" dataKey="requests" stroke="#06B6D4" fillOpacity={1} fill="url(#colorRequests)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              {/* Success Rate Pie */}
              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-white">Success Rate</h3>
                    <p className="text-gray-400 text-sm mt-1">API performance</p>
                  </div>
                  <CheckCircle className="text-green-400" size={24} />
                </div>
                <ResponsiveContainer width="100%" height={250}>
                  <PieChart>
                    <Pie
                      data={getPieData()}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                      label={({name, value}) => `${name} ${value}%`}
                    >
                      {getPieData().map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Success vs Failed & Error Breakdown */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-white">Daily Performance</h3>
                    <p className="text-gray-400 text-sm mt-1">Success vs Failed requests</p>
                  </div>
                  <Activity className="text-cyan-400" size={24} />
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={weeklyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                    <XAxis dataKey="name" stroke="#9CA3AF" />
                    <YAxis stroke="#9CA3AF" />
                    <Tooltip 
                      contentStyle={{ backgroundColor: '#1F2937', border: '1px solid #4B5563', borderRadius: '8px' }}
                      labelStyle={{ color: '#F3F4F6' }}
                    />
                    <Legend />
                    <Bar dataKey="success" fill="#10B981" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="failed" fill="#EF4444" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl p-6 hover:border-white/20 transition-all">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-bold text-white">Error Breakdown</h3>
                    <p className="text-gray-400 text-sm mt-1">Error types distribution</p>
                  </div>
                  <AlertTriangle className="text-orange-400" size={24} />
                </div>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={errorBreakdown}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({name, value}) => `${name}: ${value}%`}
                    >
                      {errorBreakdown.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recent Error Logs */}
            <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-all">
              <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white">Recent Errors</h3>
                  <p className="text-gray-400 text-sm mt-1">Latest API errors</p>
                </div>
                <Shield className="text-red-400" size={24} />
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10 bg-white/5">
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Timestamp</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Error Type</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Error Code</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Endpoint</th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-gray-300 uppercase tracking-wider">Severity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/10">
                    {analytics?.recent_errors?.slice(0, 8).map((log, idx) => (
                      <tr key={log.id || idx} className="hover:bg-white/5 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400 flex items-center gap-2">
                          <Calendar size={14} className="text-gray-500" />
                          {new Date(log.timestamp).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="text-red-300 font-mono">{parseErrorJson(log.error)}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className="bg-red-500/20 border border-red-500/30 text-red-300 px-3 py-1 rounded-full font-semibold">{log.code}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-cyan-300">{log.endpoint}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <span className={`px-3 py-1 rounded-full font-semibold text-xs ${
                            log.code >= 500 ? 'bg-red-500/20 text-red-300' : 
                            log.code >= 400 ? 'bg-yellow-500/20 text-yellow-300' : 
                            'bg-blue-500/20 text-blue-300'
                          }`}>
                            {log.code >= 500 ? 'Critical' : log.code >= 400 ? 'Warning' : 'Info'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}